import React, { useState, useEffect, useCallback } from 'react';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameMonth, addMonths, subMonths, startOfWeek, endOfWeek } from 'date-fns';
import { freeveningApi } from '../services/api';
import { CalendarDay, Freevening } from '../types';
import { parseConsistentDate, isFutureOrToday, debugDateOperation } from '../utils/dateHelpers';

const Calendar: React.FC = () => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [calendarData, setCalendarData] = useState<CalendarDay[]>([]);
  const [loading, setLoading] = useState(true);
  const [updatingDate, setUpdatingDate] = useState<string | null>(null);

  const fetchCalendarData = useCallback(async (showLoading = true) => {
    if (showLoading) setLoading(true);
    try {
      // Calculate dates inside the callback to avoid dependency issues
      const monthStart = startOfMonth(currentDate);
      const monthEnd = endOfMonth(currentDate);
      const startDate = format(monthStart, 'yyyy-MM-dd');
      const endDate = format(monthEnd, 'yyyy-MM-dd');
      
      console.log(`[API Debug] Fetching freevenings for range: ${startDate} to ${endDate}`);
      
      // Fetch both overlaps and user's own freevenings
      const [overlaps, userFreevenings] = await Promise.all([
        freeveningApi.getOverlaps(startDate, endDate),
        freeveningApi.getFreevenings(startDate, endDate)
      ]);
      
      // Debug logging for API responses
      console.log(`[API Debug] Total freevenings received:`, userFreevenings.length);
      console.log(`[API Debug] All freevenings:`, userFreevenings.map(f => ({ date: f.date, userId: f.user_id })));
      
      // Debug logging for month-end dates in the API response
      const monthEndFreevenings = userFreevenings.filter(f => {
        const dateStr = f.date.split('T')[0];
        return dateStr.endsWith('-30') || dateStr.endsWith('-31');
      });
      if (monthEndFreevenings.length > 0) {
        console.log(`[API Debug] Month-end freevenings found:`, monthEndFreevenings);
      } else {
        console.log(`[API Debug] No month-end freevenings found in response`);
      }
      
      
      // Calculate days in calendar inside the callback to avoid dependency issues
      const calendarStart = startOfWeek(monthStart);
      const calendarEnd = endOfWeek(monthEnd);
      const daysInCalendar = eachDayOfInterval({ start: calendarStart, end: calendarEnd });
      
      // Create calendar data with color coding and user availability
      const calendarDays: CalendarDay[] = daysInCalendar.map(day => {
        const dayString = format(day, 'yyyy-MM-dd');
        // Fix date format mismatch - extract date part from ISO timestamp
        const overlap = overlaps.find(o => o.date.split('T')[0] === dayString);
        const userHasFreevening = userFreevenings.some((f: Freevening) => {
          // Extract date part from ISO timestamp (e.g. "2026-05-03T00:00:00.000Z" -> "2026-05-03")
          const freeveningDate = f.date.split('T')[0];
          const match = freeveningDate === dayString;
          
          // Debug logging for month-end dates
          if (dayString.endsWith('-30') || dayString.endsWith('-31')) {
            console.log(`[Calendar Debug] Month-end date matching:`, {
              dayString,
              dayStringType: typeof dayString,
              freeveningDate,
              freeveningDateType: typeof freeveningDate,
              freeveningFull: f.date,
              match,
              userId: f.user_id,
              comparison: `'${dayString}' === '${freeveningDate}'`
            });
          }
          
          return match;
        });
        
        let color: 'red' | 'yellow' | 'green' = 'red';
        if (overlap) {
          // Fix: API returns 'count' as string, convert to number
          const overlapCount = parseInt(String(overlap.count || overlap.overlap_count || '0'), 10);
          if (overlapCount >= 3) {
            color = 'green';
          } else if (overlapCount === 2) {
            color = 'yellow';
          } else {
            color = 'red';
          }
        }
        
        return {
          date: dayString,
          color,
          overlap_count: overlap ? parseInt(String(overlap.count || overlap.overlap_count || '0'), 10) : 0,
          participants: overlap?.participants || [],
          userAvailable: userHasFreevening,
        };
      });
      
      setCalendarData(calendarDays);
    } catch (error) {
      console.error('Failed to fetch calendar data:', error);
    } finally {
      if (showLoading) setLoading(false);
    }
  }, [currentDate]);

  useEffect(() => {
    fetchCalendarData();
  }, [fetchCalendarData]);

  const navigateMonth = (direction: 'prev' | 'next') => {
    if (direction === 'prev') {
      setCurrentDate(subMonths(currentDate, 1));
    } else {
      setCurrentDate(addMonths(currentDate, 1));
    }
  };

  const handleDayClick = async (day: CalendarDay) => {
    console.log(`[Click Debug] Clicked on date: ${day.date}`);
    
    const dayDate = parseConsistentDate(day.date); // Use consistent parsing for month comparison
    const isCurrentMonth = isSameMonth(dayDate, currentDate);
    const isClickableDate = isFutureOrToday(day.date);
    
    console.log(`[Click Debug] Date analysis:`, {
      date: day.date,
      dayDate: dayDate.toString(),
      currentDate: currentDate.toString(),
      isCurrentMonth,
      isClickableDate,
      updatingDate
    });
    
    // Prevent multiple simultaneous clicks on the same date
    if (updatingDate === day.date) {
      console.log('Already updating this date, ignoring click:', day.date);
      return;
    }
    
    // Debug logging for month-end dates (especially September 30)
    if (day.date.endsWith('-30') || day.date.endsWith('-31')) {
      debugDateOperation('Month-end date click', day.date, {
        isCurrentMonth,
        isClickableDate,
        dayOfMonth: dayDate.getUTCDate(),
        month: dayDate.getUTCMonth() + 1,
        currentDateViewing: currentDate.toString(),
        clickedYear: dayDate.getFullYear(),
        currentYear: currentDate.getFullYear()
      });
    }
    
    // Only allow clicking on current month dates and future/today dates
    if (!isCurrentMonth || !isClickableDate) {
      console.log('Click blocked:', { isCurrentMonth, isClickableDate, date: day.date });
      return;
    }
    
    setUpdatingDate(day.date);
    
    // Optimistic UI update - immediately update the visual state
    const updatedCalendarData = calendarData.map(calDay => {
      if (calDay.date === day.date) {
        return { ...calDay, userAvailable: !day.userAvailable };
      }
      return calDay;
    });
    setCalendarData(updatedCalendarData);
    
    try {
      if (day.userAvailable) {
        // Remove availability
        console.log(`Attempting to remove availability for ${day.date}`);
        await freeveningApi.deleteFreeveningByDate(day.date);
      } else {
        // Add availability
        console.log(`Attempting to add availability for ${day.date}`);
        const payload = { date: day.date };
        await freeveningApi.createFreevening(payload);
      }
      
      // Refresh calendar data to get updated overlap counts and colors
      console.log(`[API Success] Refreshing calendar data after ${day.userAvailable ? 'removing' : 'adding'} availability for ${day.date}`);
      await fetchCalendarData(false);
      setUpdatingDate(null);
    } catch (error: any) {
      console.log('API call failed:', error.response?.data);
      
      // Revert the optimistic update since the API call failed
      const revertedCalendarData = calendarData.map(calDay => {
        if (calDay.date === day.date) {
          return { ...calDay, userAvailable: day.userAvailable };
        }
        return calDay;
      });
      setCalendarData(revertedCalendarData);
      
      // Handle state synchronization issues
      if (error.response?.data?.error?.includes('already marked as available')) {
        // If we tried to add but user is already available, try to remove instead
        if (!day.userAvailable) {
          console.log(`State sync issue: trying to remove availability for ${day.date} instead`);
          try {
            await freeveningApi.deleteFreeveningByDate(day.date);
            await fetchCalendarData(false);
            setUpdatingDate(null);
            return;
          } catch (deleteError: any) {
            console.log('Failed to recover from state sync issue:', deleteError);
          }
        }
      } else if (error.response?.data?.error?.includes('No availability found')) {
        // If we tried to remove but no availability found, try to add instead
        if (day.userAvailable) {
          console.log(`State sync issue: trying to add availability for ${day.date} instead`);
          try {
            const payload = { date: day.date };
            await freeveningApi.createFreevening(payload);
            await fetchCalendarData(false);
            setUpdatingDate(null);
            return;
          } catch (createError: any) {
            console.log('Failed to recover from state sync issue:', createError);
          }
        }
      }
      
      // Show user-friendly error
      console.error('Unhandled availability error:', error);
      alert(`Failed to update availability for ${day.date}. Please refresh the page and try again.`);
      setUpdatingDate(null);
    }
  };


  return (
    <div className="calendar-container">
      <div className="calendar-header">
        <button onClick={() => navigateMonth('prev')} className="nav-button">
          ←
        </button>
        <h2>{format(currentDate, 'MMMM yyyy')}</h2>
        <button onClick={() => navigateMonth('next')} className="nav-button">
          →
        </button>
      </div>

      <div className="calendar-legend">
        <div className="legend-item">
          <span className="legend-color red"></span>
          <span>0-1 people available</span>
        </div>
        <div className="legend-item">
          <span className="legend-color yellow"></span>
          <span>2 people available</span>
        </div>
        <div className="legend-item">
          <span className="legend-color green"></span>
          <span>3+ people available</span>
        </div>
        <div className="legend-item">
          <span className="availability-indicator">✓</span>
          <span>You're available</span>
        </div>
      </div>
      
      <p className="calendar-instruction">
        Click on any date to mark yourself as available for that day.
      </p>

      {loading ? (
        <div className="loading">Loading calendar...</div>
      ) : (
        <div className="calendar-grid">
          <div className="day-names">
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
              <div key={day} className="day-name">{day}</div>
            ))}
          </div>
          
          <div className="calendar-days">
            {calendarData.map(day => {
              const dayDate = parseConsistentDate(day.date); // Use consistent parsing for month comparison
              const isCurrentMonth = isSameMonth(dayDate, currentDate);
              const isClickableDate = isFutureOrToday(day.date);
              const isPast = !isClickableDate;
              const isClickable = isCurrentMonth && isClickableDate;
              
              // Debug month-end dates in render
              if (day.date.endsWith('-30') || day.date.endsWith('-31')) {
                console.log(`[Render Debug] Month-end date: ${day.date}`, {
                  dayDate: dayDate.toString(),
                  currentDate: currentDate.toString(),
                  isCurrentMonth,
                  isClickableDate,
                  isClickable,
                  isPast
                });
              }
              
              const isUpdating = updatingDate === day.date;
              
              return (
                <div
                  key={day.date}
                  className={`calendar-day ${day.color} ${isCurrentMonth ? 'current-month' : 'other-month'} ${day.userAvailable ? 'user-available' : ''} ${isPast ? 'past-date' : ''} ${isClickable ? 'clickable' : ''} ${isUpdating ? 'updating' : ''}`}
                  onClick={() => handleDayClick(day)}
                  title={isUpdating ? 'Updating...' : isClickable ? `${day.overlap_count} participant(s)${day.userAvailable ? ' - You are available (click to remove)' : ' - Click to mark as available'}` : ''}
                >
                  <span className="day-number">{format(dayDate, 'd')}</span>
                  {day.userAvailable && isCurrentMonth && <span className="availability-indicator">✓</span>}
                  {day.overlap_count > 0 && isCurrentMonth && (
                    <span className="participant-count">{day.overlap_count}</span>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

    </div>
  );
};

export default Calendar;